import React from 'react';
const { useState, useMemo, useCallback } = React;
import { keyBy } from 'lodash';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';

import Card from '../card';
import Checkbox from '../checkbox';
import Spinner from '../spinner';
import Box from '../box';


type ValueElement = string | number;

type Order = 'asc' | 'desc';

type TabPickerProps<T> = {
	value: Array<ValueElement>;
	config: Array<{sysName: string; name: string; numeric?: boolean}>;
	primaryKey: keyof T;
	initialSortKey?: string;
	initialOrder?: Order;
	dataSource: Array<T>;
	isFetching?: boolean;
	maxHeight?: number | string;
	renderMap?: Record<typeof T, (props: {value: ValueElement, isSelected: boolean}) => React.ReactNode>;
	onChange?: (e: React.SyntheticEvent, value: Array<ValueElement>) => void;
}

export type TabPickerComponent<T = any> = React.SFC<TabPickerProps<T>>;

const useStyles = makeStyles((theme) =>
  createStyles({
    table: {
      minWidth: 750,
    },
  }),
);

const TablePicker: TabPickerComponent = (props) => {
	const {
		value,
		config,
		primaryKey,
		initialSortKey,
		initialOrder,
		dataSource,
		isFetching,
		maxHeight,
		renderMap,
		onChange,
	} = props;
  const classes = useStyles({});
  const [order, setOrder] = useState<Order>(initialOrder);
	const [orderBy, setOrderBy] = useState<string>(initialSortKey || '');
	const configMap = useMemo(() => keyBy(config, x => x.sysName), [config]);

  const handleRequestSort = useCallback((_, property: string) => {
		const isDesc = orderBy === property && order === 'desc';

    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(property);
  }, [order, orderBy]);

  const handleSelectAll = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
			const newValue = dataSource.map(n => n[primaryKey]);

      onChange(e, newValue);
      return;
		}

    onChange(e, []);
  }, [dataSource]);

  const handleClick = useCallback((e: React.MouseEvent<unknown>, currentValue: ValueElement) => {
    const idx = value.indexOf(currentValue);
    let newValue = [];

    if (idx === -1) {
      newValue = newValue.concat(value, currentValue);
    } else if (idx === 0) {
      newValue = newValue.concat(value.slice(1));
    } else if (idx === value.length - 1) {
      newValue = newValue.concat(value.slice(0, -1));
    } else if (idx > 0) {
      newValue = newValue.concat(
        value.slice(0, idx),
        value.slice(idx + 1),
      );
    }

    onChange(e as any, newValue);
  }, [value]);
	
	const handleSort = (property) => (e: React.MouseEvent<unknown>) => handleRequestSort(e, property);

  return (
		<Card fullWidth padding={0} style={{ overflowX: 'auto', maxHeight }}>
			<Table className={classes.table} size='medium'>
				<TableHead>
					<TableRow>
						<TableCell style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff', padding: '16px' }}>
							<Checkbox
								checked={value.length === dataSource.length}
								onChange={handleSelectAll}
							/>
						</TableCell>
						{
							config.map(cell => (
								<TableCell
									key={cell.sysName}
									align={cell.numeric ? 'right' : 'left'}
									padding='default'
									sortDirection={orderBy === cell.sysName ? order : false}
									style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff', fontSize: 12, fontWeight: 400 }}>
									<TableSortLabel
										active={orderBy === cell.sysName}
										direction={order}
										onClick={handleSort(cell.sysName)}>
										{cell.name}
									</TableSortLabel>
								</TableCell>
							))
						}
					</TableRow>
				</TableHead>
				<TableBody>
					{
						!isFetching
						? stableSort(dataSource, getSorting(order, orderBy))
							.map(row => {
								const isSelected = value.indexOf(row[primaryKey]) !== -1;

								return (
									<TableRow
										hover
										onClick={e => handleClick(e, row[primaryKey])}
										role='checkbox'
										aria-checked={isSelected}
										tabIndex={-1}
										key={row[primaryKey]}
										selected={isSelected}
										style={{ cursor: 'pointer' }}>
										<TableCell style={{ padding: '16px' }}>
											<Checkbox checked={isSelected} onChange={() => {}} />
										</TableCell>
										{
											Object.keys(row).map(key => {
												if (key === primaryKey) return null;
												const isNumeric = configMap[key].numeric;

												return (
													<TableCell align={isNumeric ? 'right' : 'left'} key={key}>
														{
															typeof renderMap[key] === 'function'
																? renderMap[key]({ value: row[key], isSelected })
																: row[key]
														}
													</TableCell>
												);
											})
										}
									</TableRow>
								);
							})
						: <TableRow>
								<TableCell
									style={{ padding: '16px' }}
									colSpan={config.length + 1}>
									<Box display='flex' justifyContent='center' alignItems='center' minHeight={100}>
										<Spinner />
									</Box>
								</TableCell>
							</TableRow>
					}
				</TableBody>
			</Table>
		</Card>
  );
}

TablePicker.displayName = 'TablePicker';
TablePicker.defaultProps = {
	maxHeight: 300,
	renderMap: {},
	initialOrder: 'desc',
};

function desc<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
	}

  if (b[orderBy] > a[orderBy]) {
    return 1;
	}

  return 0;
}

function stableSort<T>(array: T[], cmp: (a: T, b: T) => number) {
	const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);

  stabilizedThis.sort((a, b) => {
		const order = cmp(a[0], b[0]);

		if (order !== 0) return order;

    return a[1] - b[1];
	});

  return stabilizedThis.map(el => el[0]);
}

function getSorting<K extends keyof any>(
  order: Order,
  orderBy: K,
): (a: { [key in K]: number | string }, b: { [key in K]: number | string }) => number {
  return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

export default TablePicker;
